import { Box, Grid, InputAdornment, Paper, TextField, Typography } from "@mui/material";
import { useData } from "../../utils/useData";
import React, { useState } from "react";
import { CurrentFloatView } from "../../components/billing/CurrentFloatView";
import { LoadingButton } from "@mui/lab";
import { useMutation } from "react-query";
import { Invoice, InvoiceType } from "../../models";
import { toast } from "react-toastify";
import { useConfig } from "../../utils/useConfig";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { PaymentDialog } from "../../components/billing/PaymentDialog";

type ConfirmTopUpVariables = {
  invoiceId: string;
};

export const FloatManagement = () => {
  const { float, employer, invoices } = useData();
  const { config } = useConfig();
  const { getToken } = useKindeAuth();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [error, setError] = useState<string | undefined>(undefined);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState<boolean>(false);
  const [processingPayment, setProcessingPayment] = useState<boolean>(false);

  const createFloatInvoice = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${config?.API_URL}/employers/${employer.data!.id}/invoices`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${await getToken()}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          "type": InvoiceType.FLOAT,
          "amount": amount
        })
      });

      if (!response.ok) {
        throw new Error("There was a problem processing the payment");
      }

      const invoice = await response.json();
      setInvoice(invoice);
    },
    onSuccess: () => {
      setPaymentDialogOpen(true);
    },
    onError: (error: Error) => {
      console.error(error.message);
      setError(error.message);
    }
  });

  const confirmTopUp = useMutation<void, Error, ConfirmTopUpVariables>(async ({ invoiceId }) => {
      const response = await fetch(`${config?.API_URL}/employers/${employer.data!.id}/float/top-ups`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${await getToken()}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          "invoiceId": invoiceId,
          "amount": amount
        })
      });

      if (!response.ok) {
        throw new Error("There was a problem processing the payment");
      }
    },
    {
      onSuccess: () => {
        setPaymentDialogOpen(false);
      },
      onError: (error: Error) => {
        console.error(error.message);
        setError(error.message);
      }
    }
  );

  if (float.isLoading || float.isIdle || employer.isLoading) {
    return <p>Loading...</p>;
  }

  if (float.error || !float.data || employer.error || !employer.data) {
    return <p>Something went wrong, please try again later</p>;
  }

  const handleAmountChange = (event: any) => {
    const value = parseFloat(event.target.value.replace(/[^0-9.-]+/g, ""));
    if (!isNaN(value) && value >= 0) {
      setAmount(value);
    }
  };

  const handleBankTransferPaymentSent = async () => {
    setError(undefined);
    if (amount <= 0) {
      setError("Amount needs to be positive number");
      return;
    }
    setProcessingPayment(true);
    await createFloatInvoice.mutateAsync();
  };

  const onSuccessfulPayment = async () => {
    if (invoice == null) {
      return;
    }

    try {
      await confirmTopUp.mutateAsync({ invoiceId: invoice.id});
      await invoices.refetch();
      await float.refetch();
      setPaymentDialogOpen(false);
      setProcessingPayment(false);
    } catch (e) {

    }
  };

  return (
    <>
      {invoice != null &&
        <PaymentDialog
          amount={invoice.amount}
          reference={employer.data!.name}
          title={`Float top up`}
          open={paymentDialogOpen}
          invoiceId={invoice.id}
          onCancel={() => {
            setPaymentDialogOpen(false);
            setProcessingPayment(false);
          }}
          onSuccess={onSuccessfulPayment}
        />
      }
      <Grid container spacing={5} flexDirection="column">
        <Grid item>
          <Box minWidth="fit-content">
            <Typography variant="h4">Billing & Invoicing</Typography>
          </Box>
        </Grid>
        <Grid item>
          <Box minWidth="fit-content">
            <CurrentFloatView float={float.data} />
          </Box>
        </Grid>
        <Grid
          item
          sx={{ display: "flex", justifyContent: "center" }}
          spacing={2}
          justifyContent="center">
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Paper elevation={0} sx={{ p: 5, width: "100%", maxWidth: 600 }}>
              <Box minWidth="fit-content">
                <Typography variant="h6" pb={2}>
                  To ensure your services continue, top-up your float to the required minimum amount.
                </Typography>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ m: 0, p: 3 }}
                >
                  <TextField
                    value={amount}
                    onChange={handleAmountChange}
                    label="Amount"
                    required
                    type="number"
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      inputMode: "numeric"
                    }}
                  />
                </Box>
                {error &&
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ m: 0, p: 3 }}
                  >
                    <Typography color={"error"}>{error}</Typography>
                  </Box>
                }
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ m: 0 }}
                >
                  <LoadingButton onClick={handleBankTransferPaymentSent}
                                 variant="contained"
                                 color="primary"
                                 loading={processingPayment || createFloatInvoice.isLoading || employer.isLoading || employer.data === undefined}
                                 autoFocus>
                    Top Up
                  </LoadingButton>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
        <Grid item>
          <Box minWidth="fit-content">
            <Typography variant="h6" pb={2}>
              XXXX
            </Typography>

          </Box>
        </Grid>
      </Grid>
    </>
  );
};
